import { AccountingSubType } from "@entities/accounting/lib/constants";
import { useCreateInvoice } from "@entities/accounting/lib/hooks/useCreateInvoice";
import { useGetBrands } from "@entities/brand";
import { useGetBriefs } from "@entities/brief/lib/hooks/useGetBriefs";
import { useGetBriefsLedgers } from "@entities/brief/lib/hooks/useGetBriefsLedgers";
import { Money } from "@shared/lib/services/Money.service";
import { IBriefLedger, LEDGER_TYPE } from "@shared/lib/types/briefLedger";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDebounce } from "use-debounce";

const STEPS = 4;

export function useAddBrandAccountingState(onClose: () => void) {
  const [step, setStep] = useState(1);
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>([]);
  const [selectedPurchases, setSelectedPurchases] = useState<IBriefLedger[]>(
    []
  );

  const isLedgerInitialDataSet = useRef<boolean>(false);
  const [billingType, setBillingType] = useState<AccountingSubType>(
    AccountingSubType.invoice
  );

  const [brandSearch, setBrandSearch] = useState("");
  const [debouncedBrandSearch] = useDebounce(brandSearch, 1000);
  const [campaignSearch, setCampaignSearch] = useState("");
  const [debouncedCampaignSearch] = useDebounce(campaignSearch, 1000);
  const [purchaseSearch, setPurchaseSearch] = useState("");
  const [debouncedPurchaseSearch] = useDebounce(purchaseSearch, 1000);

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [invoiceAmount, setInvoiceAmount] = useState("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const {
    create: createInvoice,
    isCreating: isCreatingInvoice,
    isCreated: isCreatedInvoice,
  } = useCreateInvoice(
    () => {
      setIsSubmitting(false);
      setShowSuccess(true);
      setTimeout(() => {
        onClose();
        reset();
      }, 2000);
    },
    () => {
      setIsSubmitting(false);
      setShowSuccess(false);
    }
  );

  const { brands, isFetching: isFetchingBrands } = useGetBrands(
    10,
    1,
    debouncedBrandSearch
  );

  const { briefs, isFetching: isFetchingBriefs } = useGetBriefs({
    limit: 10,
    pageNo: 1,
    searchQuery: debouncedCampaignSearch,
    campaignByBrandIds: selectedBrand ? [selectedBrand] : undefined,
    mainStatus: ["Campaign"],
  });

  const { ledgers: purchases, isFetching: isFetchingLedgers } =
    useGetBriefsLedgers({
      briefIds: selectedCampaigns,
      searchQuery: debouncedPurchaseSearch,
    });

  const totalPurchaseCost = selectedPurchases
    .reduce(
      (acc, curr) =>
        curr.type === LEDGER_TYPE.Credit
          ? acc.subtract(new Money(curr.baseCost))
          : acc.add(new Money(curr.baseCost)),
      new Money(0)
    )
    .toNumber();

  const totalSellingCost = selectedPurchases
    .reduce(
      (acc, curr) =>
        curr.type === LEDGER_TYPE.Credit
          ? acc.subtract(new Money(curr.sellingCost))
          : acc.add(new Money(curr.sellingCost)),
      new Money(0)
    )
    .toNumber();

  const totalServiceCost = selectedPurchases
    .reduce(
      (acc, curr) =>
        curr.type === LEDGER_TYPE.Credit
          ? acc.subtract(new Money(curr.serviceCost))
          : acc.add(new Money(curr.serviceCost)),
      new Money(0)
    )
    .toNumber();

  const totalGst = selectedPurchases
    .reduce(
      (acc, curr) =>
        curr.type === LEDGER_TYPE.Credit
          ? acc.subtract(new Money(curr.gstAmount))
          : acc.add(new Money(curr.gstAmount)),
      new Money(0)
    )
    .toNumber();

  const grandTotal = selectedPurchases
    .reduce(
      (acc, curr) =>
        curr.type === LEDGER_TYPE.Credit
          ? acc.subtract(new Money(curr.totalAmount))
          : acc.add(new Money(curr.totalAmount)),
      new Money(0)
    )
    .toNumber();

  const handleNext = useCallback(() => {
    if (step < STEPS) setStep((s) => s + 1);
  }, [step]);

  const handleBack = useCallback(() => {
    if (step > 1) setStep((s) => s - 1);
  }, [step]);

  const toggleSelectAllPurchases = useCallback(() => {
    setSelectedPurchases((prev) =>
      prev.length === purchases.length ? [] : purchases
    );
  }, [purchases]);

  useEffect(() => {
    if (
      !isFetchingLedgers &&
      !isLedgerInitialDataSet.current &&
      purchases.length > 0
    ) {
      isLedgerInitialDataSet.current = true;
      setSelectedPurchases(purchases);
    }
  }, [isFetchingLedgers, isLedgerInitialDataSet.current, purchases]);

  useEffect(() => {
    if (isLedgerInitialDataSet.current) {
      isLedgerInitialDataSet.current = false;
    }
  }, [selectedBrand, selectedCampaigns]);

  useEffect(() => {
    setSelectedCampaigns([]);
    setSelectedPurchases([]);
  }, [selectedBrand]);

  const reset = useCallback(() => {
    setStep(1);
    setSelectedBrand("");
    setSelectedCampaigns([]);
    setSelectedPurchases([]);
    setBillingType(AccountingSubType.invoice);
    setInvoiceNumber("");
    setInvoiceDate("");
    setInvoiceAmount("");
    setUploadedFile(null);
    setUploadedFileUrl(null);
    setShowSuccess(false);
    setBrandSearch("");
    setCampaignSearch("");
    setPurchaseSearch("");
    setIsSubmitting(false);
    isLedgerInitialDataSet.current = false;
  }, []);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const expectedTotal = new Money(Math.abs(grandTotal)).toNumber();
    const parsedInvoice = Number(invoiceAmount);
    if (
      invoiceAmount !== "" &&
      !Number.isNaN(parsedInvoice) &&
      new Money(Math.abs(parsedInvoice)).toNumber() !== expectedTotal
    ) {
      return {
        error: `The total amount (₹${invoiceAmount}) must match the calculated grand total (₹${expectedTotal.toLocaleString()}).`,
      };
    }

    setIsSubmitting(true);
    createInvoice({
      baseAmount: Math.abs(new Money(Math.abs(totalPurchaseCost)).toNumber()),
      gstAmount: Math.abs(new Money(Math.abs(totalGst)).toNumber()),
      totalAmount: Math.abs(new Money(Math.abs(grandTotal)).toNumber()),
      brandId: selectedBrand,
      brandLedgerIds: selectedPurchases.map((p) => p._id),
      briefIds: selectedCampaigns,
      subType: billingType,
      accountingNumber: invoiceNumber,
      url: uploadedFileUrl || undefined,
      date: new Date(invoiceDate).toISOString(),
    });
    return { error: null };
  };

  const canProceed =
    (step === 1 && !!selectedBrand) ||
    (step === 2 && selectedCampaigns.length > 0) ||
    (step === 3 && selectedPurchases.length > 0);

  const isStep4Valid =
    !!invoiceNumber &&
    !!invoiceDate &&
    invoiceAmount !== "" &&
    !Number.isNaN(Number(invoiceAmount)) &&
    !!uploadedFile &&
    Number(invoiceAmount) === grandTotal;

  return {
    step,
    setStep,
    selectedBrand,
    setSelectedBrand,
    selectedCampaigns,
    setSelectedCampaigns,
    selectedPurchases,
    setSelectedPurchases,
    billingType,
    setBillingType,

    brandSearch,
    setBrandSearch,
    campaignSearch,
    setCampaignSearch,
    purchaseSearch,
    setPurchaseSearch,
    invoiceNumber,
    setInvoiceNumber,
    invoiceDate,
    setInvoiceDate,
    invoiceAmount,
    setInvoiceAmount,
    uploadedFile,
    setUploadedFile,
    uploadedFileUrl,
    setUploadedFileUrl,
    isSubmitting,
    showSuccess,
    filteredBrands: brands?.data || [],
    filteredCampaigns: briefs || [],
    filteredPurchases: purchases || [],
    totalPurchaseCost,
    totalSellingCost,
    totalServiceCost,
    totalGst,

    grandTotal,
    handleNext,
    handleBack,

    toggleSelectAllPurchases,
    handleSubmit,
    canProceed,
    isStep4Valid,
    reset,
    isFetchingBrands,
    isFetchingBriefs,
  };
}
